import { useMemo } from 'react';
import { useParams } from 'react-router';
import { useAdminJob, useAdminRuns, useAdminUser } from '../api/admin';
import { RelativeTime } from '../components/RelativeTime';
import { StateBadge } from '../components/StateBadge';
import {
  AdminError,
  AdminSection,
  AdminShell,
  DetailList,
  AdminRunsTable,
  AdminSkeletonRows,
  UserLink,
} from './adminShared';

export function AdminJobPage() {
  const { jobId } = useParams<{ jobId: string }>();
  const job = useAdminJob(jobId);
  // Guard: without `enabled`, an undefined jobId would degenerate the filter to
  // `{}` and silently fetch ALL runs. (Unreachable via the /admin/jobs/:jobId
  // route, but cheap belt-and-braces.)
  const runs = useAdminRuns({ jobId }, { enabled: !!jobId });
  const owner = useAdminUser(job.data?.userId);
  const title = job.data?.repo ?? job.data?.jiraIssueKey ?? 'Job';
  const userEmails = useMemo(
    () => (owner.data ? new Map([[owner.data.id, owner.data.email]]) : undefined),
    [owner.data],
  );

  return (
    <AdminShell title={title} subtitle={jobId}>
      <AdminSection title="Job">
        {job.isPending && <AdminSkeletonRows rows={4} />}
        {job.error && (
          <AdminError subject="job" error={job.error} onRetry={() => void job.refetch()} />
        )}
        {job.data && (
          <DetailList
            rows={[
              ['State', <StateBadge state={job.data.state} />],
              ['Prompt', job.data.prompt ?? ''],
              ['User', <UserLink userId={job.data.userId} email={owner.data?.email} />],
              ['Source', job.data.source],
              ['Mode', job.data.mode ?? ''],
              ['Jira', job.data.jiraIssueKey ?? ''],
              ['Branch', job.data.branch ?? ''],
              ['Created', <RelativeTime iso={job.data.createdAt} />],
              ['Updated', <RelativeTime iso={job.data.updatedAt} />],
            ]}
          />
        )}
      </AdminSection>

      <AdminSection title="Runs">
        {runs.isPending && <AdminSkeletonRows />}
        {runs.error && (
          <AdminError subject="runs" error={runs.error} onRetry={() => void runs.refetch()} />
        )}
        {runs.data && <AdminRunsTable runs={runs.data} userEmails={userEmails} />}
      </AdminSection>
    </AdminShell>
  );
}
